import { API_ENDPOINTS } from '../constants/api';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { getSignedUrls } from '../utils/fileOperations';
import { reportError } from '../sentry';

class ReferralService {
  /**
   * Fetch referral rules
   * @returns {Promise<Object>} Response data containing referral rules
   */
  async getReferralRules() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.REFERRAL_RULES);
      return ResponseHandler.handleSuccess(response, 'Referral rules fetched successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch referral rules');
    }
  }

  /**
   * Fetch current bonuses for the user
   * @returns {Promise<Object>} Response data containing current bonuses
   */
  async getMyCurrentBonuses() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.REFERRAL_BONUSES);
      return ResponseHandler.handleSuccess(response, 'Current bonuses fetched successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch current bonuses');
    }
  }

  /**
   * Fetch bonus history for the user
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Response data containing bonus history
   */
  async getMyBonusHistory(params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.REFERRAL_BONUS_HISTORY, {
        params
      });

      // Process user.profile_picture with signed URLs (not referred.profile_picture)
      if (response.data?.data && Array.isArray(response.data.data)) {
        const dataArray = response.data.data;
        
        // Extract all unique user.profile_picture keys that need signed URLs
        const profileKeys = dataArray
          .map(item => item?.user?.profile_picture)
          .filter(key => 
            key && 
            typeof key === 'string' && 
            key.trim() !== '' && 
            !key.startsWith('http')
          );

        if (profileKeys.length > 0) {
          const uniqueKeys = [...new Set(profileKeys)];
          try {
            const signedUrlsData = await getSignedUrls(uniqueKeys, true);
            const signedUrlMap = {};
            signedUrlsData.forEach(({ key, signedUrl }) => {
              signedUrlMap[key] = signedUrl;
            });

            // Replace user.profile_picture with signed URLs
            dataArray.forEach((item) => {
              if (item?.user?.profile_picture) {
                const profilePicture = item.user.profile_picture;
                if (typeof profilePicture === 'string' && 
                    profilePicture.trim() !== '' && 
                    !profilePicture.startsWith('http') &&
                    signedUrlMap[profilePicture]) {
                  item.user.profile_picture = signedUrlMap[profilePicture];
                }
              }
            });
          } catch (error) {
            reportError(error, 'signed URLs user profile pictures');
            // Continue with original response if signed URL fetch fails
          }
        }
      }

      const result = ResponseHandler.handleSuccess(response, 'Bonus history fetched successfully');
      // Include pagination information
      if (response.data) {
        result.current_page = response.data.current_page;
        result.total_pages = response.data.total_pages;
        result.total_records = response.data.total_records;
      }

      return result;
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch bonus history');
    }
  }

  /**
   * Fetch referral amount
   * @returns {Promise<Object>} Response data containing referral amount
   */
  async getReferralAmount() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.REFERRAL_AMOUNT);
      return ResponseHandler.handleSuccess(response, 'Referral amount fetched successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch referral amount');
    }
  }
}

export default new ReferralService();

